class RecommendationTabs extends HTMLElement {
  /** @type {(() => void) | null} */
  #resizeListener = null;

  connectedCallback() {
    const tabs = this.querySelectorAll('[data-recommendation-tab]');
    const panels = this.querySelectorAll('[data-recommendation-panel]');

    if (!tabs.length || !panels.length) return;

    // Hide tabs whose panel has no products, so customers never land on an empty tab.
    /** @type {HTMLElement | null} */
    let firstVisibleTab = null;
    tabs.forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      const panel = this.querySelector(`[data-recommendation-panel="${tab.dataset.recommendationTab}"]`);
      const isEmpty = !panel || !panel.querySelector('[data-has-recommendations="true"]');
      tab.hidden = isEmpty;
      tab.style.display = isEmpty ? 'none' : '';
      if (!isEmpty && !firstVisibleTab) firstVisibleTab = tab;
    });

    if (!firstVisibleTab) {
      const menu = this.querySelector('.recommendation-tabs__menu');
      if (menu instanceof HTMLElement) menu.hidden = true;
      return;
    }

    this.#activate(firstVisibleTab.dataset.recommendationTab);

    tabs.forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;

      tab.addEventListener('click', () => {
        const tabId = tab.dataset.recommendationTab;
        if (!tabId) return;
        this.#activate(tabId);
      });
    });

    this.#resizeListener = () => {
      this.#updateCarouselArrows();
    };

    window.addEventListener('resize', this.#resizeListener);
    requestAnimationFrame(() => {
      this.#updateCarouselArrows();
    });
  }

  disconnectedCallback() {
    if (this.#resizeListener) {
      window.removeEventListener('resize', this.#resizeListener);
      this.#resizeListener = null;
    }
  }

  /**
   * @param {string | undefined} tabId
   */
  #activate(tabId) {
    if (!tabId) return;

    this.querySelectorAll('[data-recommendation-tab]').forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;

      const isActive = tab.dataset.recommendationTab === tabId;
      tab.classList.toggle('recommendation-tabs__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.querySelectorAll('[data-recommendation-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      const isActive = panel.dataset.recommendationPanel === tabId;
      panel.classList.toggle('recommendation-tabs__panel--active', isActive);
      panel.hidden = !isActive;
    });

    requestAnimationFrame(() => {
      this.#updateCarouselArrows();
    });
  }

  #updateCarouselArrows() {
    const activePanel = this.querySelector('[data-recommendation-panel]:not([hidden])');
    if (!(activePanel instanceof HTMLElement)) return;

    activePanel.querySelectorAll('.recommendation-tabs__carousel slideshow-component').forEach((slideshow) => {
      const scroller = slideshow.querySelector('slideshow-slides');
      const arrows = slideshow.querySelector('slideshow-arrows');

      if (!(scroller instanceof HTMLElement) || !(arrows instanceof HTMLElement)) return;

      const needsNavigation = scroller.scrollWidth > Math.ceil(scroller.clientWidth + 1);
      arrows.hidden = !needsNavigation;
    });
  }
}

if (!customElements.get('recommendation-tabs')) {
  customElements.define('recommendation-tabs', RecommendationTabs);
}
